import { ConfigPaths } from "@omni/types/utils/configPaths";

import { isDefined } from "./helper.utils";

export const omit = <T extends object, K extends keyof T>(
    obj: T,
    toOmit: Set<keyof T> | Array<keyof T>
): Omit<T, K> => {
    const toOmitSet = Array.isArray(toOmit) ? new Set(toOmit) : toOmit;

    return keys(obj).reduce((acc, val) => {
        if (!toOmitSet.has(val)) {
            acc[val] = obj[val];
        }

        return acc;
    }, {} as T);
};

export const pick = <T extends object, K extends keyof T>(
    obj: T,
    toPick: Set<K> | Array<K>
) => {
    const toPickArray = Array.isArray(toPick)
        ? toPick
        : toPick.values().toArray();

    return toPickArray.reduce<Pick<T, K>>(
        (acc, val) => {
            acc[val] = obj[val];
            return acc;
        },
        {} as Pick<T, K>
    );
};

export const has = <T extends object>(obj: T, key: ConfigPaths<T>): boolean => {
    if (!isDefined(obj) || !isDefined(key)) {
        return false;
    }

    const keyParts = key.split(".");
    let current: unknown = obj;

    for (const part of keyParts) {
        if (!isDefined(current) || typeof current !== "object") {
            return false;
        }

        if (!Object.hasOwn(current as object, part)) {
            return false;
        }

        current = (current as Record<string, unknown>)[part];
    }

    return true;
};

export const get = <T extends object>(obj: T, path: ConfigPaths<T>) => {
    return path
        .split(".")
        .reduce((o, k) => (o ? (o as any)[k] : undefined), obj);
};

export const cleanUpObjRecursive = (obj: any): any => {
    const tempObj = obj;
    for (const key in tempObj) {
        if (typeof tempObj[key] === "object" && tempObj[key] !== null) {
            if (!Object.keys(tempObj).length) {
                delete tempObj[key];
            } else {
                cleanUpObjRecursive(tempObj[key]);
            }
        } else if (tempObj[key] === undefined || tempObj[key] === null) {
            delete tempObj[key];
        }
    }

    return tempObj;
};

export const cleanObject = <T extends Record<string, any>>(
    obj: T
): Partial<T> => {
    return entries(obj)
        .filter(([, value]) => value !== undefined)
        .reduce<Partial<T>>((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});
};

export const removeKeysMatching = (
    obj: any,
    predicate: (key: string) => boolean
) => {
    Object.keys(obj).forEach((key) => predicate(key) && delete obj[key]);
    return obj;
};

export const mergeLeft = <TReturn extends object, TToMerge extends object>(
    base: TReturn,
    toMerge: TToMerge
) =>
    Object.keys(base).reduce(
        (merged, key) => ({
            ...merged,
            [key as keyof typeof base]:
                toMerge[key as keyof typeof toMerge] ??
                base[key as keyof typeof base]
        }),
        {} as TReturn
    );

export const partitionAndFind = <T>(
    collection: T[],
    predicate: (element: T) => boolean
) =>
    collection.reduce(
        ([matchedValue, rest], value) => {
            if (isDefined(matchedValue))
                return [matchedValue, [...rest, value]] as const;

            return predicate(value)
                ? ([value, rest] as const)
                : ([undefined, [...rest, value]] as const);
        },
        [undefined, []] as readonly [undefined | T, readonly T[]]
    );

export const keys = <T extends object>(obj: T) => {
    return Object.keys(obj) as (keyof T)[];
};

export const allKeys = <T extends object>(obj: T) => {
    const result = new Set<string>();

    Object.keys(obj).reduce((acc, key) => {
        acc.add(key);

        const val = obj[key as keyof T];

        if (typeof val === "object" && val !== null) {
            allKeys(val)
                .map((k) => `${key}.${k}`)
                .forEach((nestedKey) => acc.add(nestedKey));
        }

        return acc;
    }, result);

    return Array.from(result);
};

export const values = <T extends object>(obj: T) => {
    return Object.values(obj) as T[keyof T][];
};

export const entries = <T extends object, TKey extends keyof T = keyof T>(
    obj: T
) => {
    return Object.entries(obj) as [TKey, T[keyof T]][];
};

export const fromEntries = <
    T extends readonly (readonly [PropertyKey, unknown])[]
>(
    e: T
): {
    [K in T[number] as K[0]]: K[1];
} => {
    return Object.fromEntries(e) as any;
};

export const include = <T, U>(
    value: T | undefined | null,
    wrap: (v: T) => U
) => {
    return isDefined(value) ? wrap(value) : {};
};

export const includeArray = <T, U>(
    value: T | null | undefined,
    wrap: (v: T) => U[]
): U[] => {
    return isDefined(value) ? wrap(value) : [];
};

export const isKey = <T extends Record<string, string | number>>(
    obj: T,
    value: unknown
): value is keyof T => {
    return keys(obj).includes(value as keyof T);
};
